import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Camera, Filter, Phone, MapPin } from "lucide-react";
import { Header, Footer, StickyCTA } from "../components/Layout";

// Toutes les photos d'interventions réelles avec alt SEO optimisés
const INTERVENTIONS = [
  {
    image: "/images/9zoguxx1.webp",
    titre: "Intervention en hauteur",
    description: "Notre technicien retire un nid de frelons dans un arbre à Nice. Intervention sécurisée avec équipement professionnel.",
    alt: "Technicien Azur Tech retirant un nid de frelons dans un arbre à Nice avec équipement de protection",
    type: "Guêpes/Frelons",
    lieu: "Nice"
  },
  {
    image: "/images/4bp40k67.webp",
    titre: "Nid de frelons géant",
    description: "Retrait d'un nid de frelons de 60cm sur un balcon. Intervention d'urgence réalisée le jour même.",
    alt: "Nid de frelons géant de 60cm retiré sur un balcon à Nice par Azur Tech",
    type: "Guêpes/Frelons",
    lieu: "Nice"
  },
  {
    image: "/images/9tra3g8h.webp",
    titre: "Nid de guêpes au plafond",
    description: "Destruction d'un nid de guêpes installé au plafond d'un balcon. Zone sécurisée avant intervention.",
    alt: "Destruction nid de guêpes au plafond balcon Cannes - intervention Azur Tech",
    type: "Guêpes/Frelons",
    lieu: "Cannes"
  },
  {
    image: "/images/81sqkr4m.webp",
    titre: "Nid de guêpes sous auvent",
    description: "Nid de guêpes dissimulé sous un auvent. Traitement et retrait complet effectués.",
    alt: "Nid de guêpes sous auvent traité et retiré à Antibes par expert Azur Tech",
    type: "Guêpes/Frelons",
    lieu: "Antibes"
  },
  {
    image: "/images/puz3q051.webp",
    titre: "Équipement professionnel",
    description: "Notre matériel de traitement : pulvérisateur professionnel et masque de protection pour interventions sécurisées.",
    alt: "Équipement professionnel désinsectisation Azur Tech - pulvérisateur et masque protection",
    type: "Équipement",
    lieu: "Nice"
  },
  {
    image: "/images/igxsoswd.webp",
    titre: "Infestation sévère punaises",
    description: "Matelas fortement infesté de punaises de lit. Traitement thermique et chimique combiné pour éradication totale.",
    alt: "Matelas infesté punaises de lit Nice - avant traitement thermique Azur Tech",
    type: "Punaises de lit",
    lieu: "Nice"
  },
  {
    image: "/images/hld6i9zq.webp",
    titre: "Punaise de lit détectée",
    description: "Punaise de lit visible dans la couture du matelas lors du diagnostic. Identification précise avant traitement.",
    alt: "Punaise de lit détectée couture matelas Cannes - diagnostic Azur Tech",
    type: "Punaises de lit",
    lieu: "Cannes"
  },
  {
    image: "/images/xtkwzabd.webp",
    titre: "Matelas infesté",
    description: "Traces de punaises de lit sur matelas. Traitement complet de la literie et des textiles environnants.",
    alt: "Traces punaises de lit sur matelas Menton - traitement complet Azur Tech",
    type: "Punaises de lit",
    lieu: "Menton"
  },
  {
    image: "/images/liaj3t7n.webp",
    titre: "Infestation cafards",
    description: "Prise électrique infestée de cafards. Désinsectisation complète avec traitement gel longue durée.",
    alt: "Prise électrique infestée cafards Nice - désinsectisation gel Azur Tech",
    type: "Désinsectisation",
    lieu: "Nice"
  }
];

// Catégories pour le filtre
const CATEGORIES = ["Tous", "Guêpes/Frelons", "Punaises de lit", "Désinsectisation", "Équipement"];

function Galerie() {
  const [selectedCategory, setSelectedCategory] = useState("Tous");
  const [selectedImage, setSelectedImage] = useState(null);

  const filteredInterventions = selectedCategory === "Tous" 
    ? INTERVENTIONS 
    : INTERVENTIONS.filter(i => i.type === selectedCategory);

  return (
    <div className="min-h-screen bg-gray-50 pb-16 md:pb-0">
      <Helmet>
        <title>Galerie Photos Interventions | Azur Tech Nice - Guêpes, Punaises, Cafards</title>
        <meta name="description" content="Photos réelles de nos interventions à Nice et PACA. Destruction nids de guêpes, traitement punaises de lit, désinsectisation cafards. Voir notre travail ☎ 07 64 07 95 35" />
        <link rel="canonical" href="https://azurtech06.fr/galerie" />
      </Helmet>
      <Header />

      {/* Hero */}
      <section className="pt-28 md:pt-36 pb-12 bg-gradient-to-br from-[#0f172a] to-[#1e3a5f]">
        <div className="container mx-auto px-4 text-center">
          <div className="inline-flex items-center bg-emerald-500/20 text-emerald-400 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Camera className="w-4 h-4 mr-2" />
            Photos réelles de nos chantiers
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            Galerie d'interventions
          </h1>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            Découvrez nos interventions réelles à Nice et dans les Alpes-Maritimes. Chaque photo témoigne de notre expertise et de notre professionnalisme.
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="py-8 bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-8 md:gap-16">
            <div className="text-center">
              <p className="text-3xl font-bold text-emerald-500">{INTERVENTIONS.length}</p>
              <p className="text-gray-600 text-sm">Photos de chantiers</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-emerald-500">+5000</p>
              <p className="text-gray-600 text-sm">Interventions réalisées</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-emerald-500">100%</p>
              <p className="text-gray-600 text-sm">Clients satisfaits</p>
            </div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-6 bg-gray-100 sticky top-20 z-30">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center flex-wrap gap-2">
            <Filter className="w-5 h-5 text-gray-500 mr-2 hidden sm:block" />
            {CATEGORIES.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === category
                    ? "bg-emerald-500 text-white shadow-lg"
                    : "bg-white text-gray-700 hover:bg-gray-200"
                }`}
                data-testid={`filter-${category.toLowerCase().replace(/\//g, '-')}`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredInterventions.map((intervention, idx) => (
              <div 
                key={idx} 
                className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all cursor-pointer"
                onClick={() => setSelectedImage(intervention)}
                data-testid={`gallery-item-${idx}`}
              >
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={intervention.image} 
                    alt={intervention.alt || intervention.titre}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="absolute bottom-4 left-4 right-4">
                      <p className="text-white font-bold">{intervention.titre}</p>
                      <p className="text-white/80 text-sm flex items-center mt-1">
                        <MapPin className="w-3 h-3 mr-1" />
                        {intervention.lieu}
                      </p>
                    </div>
                  </div>
                  <div className="absolute top-3 left-3">
                    <span className="bg-emerald-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                      {intervention.type}
                    </span>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-bold text-lg text-[#0f172a] mb-2">{intervention.titre}</h3>
                  <p className="text-gray-600 text-sm line-clamp-2">{intervention.description}</p>
                  <div className="flex items-center mt-3 text-sm text-gray-500">
                    <MapPin className="w-4 h-4 mr-1" />
                    {intervention.lieu}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredInterventions.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">Aucune photo dans cette catégorie.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-emerald-500 to-emerald-600">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Un problème similaire ?
          </h2>
          <p className="text-lg text-emerald-100 mb-8 max-w-2xl mx-auto">
            Que ce soit pour des guêpes, des punaises de lit ou des cafards, nous intervenons rapidement à Nice et dans toute la région PACA.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:0764079535"
              className="inline-flex items-center justify-center bg-white text-emerald-600 px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transition-all shadow-lg"
            >
              <Phone className="w-5 h-5 mr-2" />
              07 64 07 95 35
            </a>
            <a
              href="/#devis"
              className="inline-flex items-center justify-center bg-emerald-700 text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-emerald-800 transition-all"
            >
              Demander un devis gratuit
            </a>
          </div>
        </div>
      </section>

      {/* Modal Lightbox */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div 
            className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <img 
                src={selectedImage.image} 
                alt={selectedImage.alt || selectedImage.titre}
                className="w-full h-auto max-h-[60vh] object-contain bg-gray-100"
              />
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute top-4 right-4 bg-black/50 text-white w-10 h-10 rounded-full flex items-center justify-center hover:bg-black/70 transition-colors"
              >
                ✕
              </button>
              <div className="absolute top-4 left-4">
                <span className="bg-emerald-500 text-white text-sm font-semibold px-4 py-2 rounded-full">
                  {selectedImage.type}
                </span>
              </div>
            </div>
            <div className="p-6">
              <h3 className="text-2xl font-bold text-[#0f172a] mb-2">{selectedImage.titre}</h3>
              <p className="text-gray-600 mb-4">{selectedImage.description}</p>
              <div className="flex items-center text-gray-500">
                <MapPin className="w-5 h-5 mr-2" />
                <span>{selectedImage.lieu}, Alpes-Maritimes</span>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
      <StickyCTA />
    </div>
  );
}

export default Galerie;
